import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { SuggestBox, ReactDatePicker } from '@mummssoftware/common-ui';
import { AppContext } from '../../App/AppContext';
import { CertContext, CertDispatchContext } from '../../../Context';
import { getDateLimit } from '../../utils/utils';
import {
  SET_DOCTOR,
  SET_DATE,
  SET_VERBAL_DATE_LIMIT,
  SET_LATE_CERT,
} from '../../../constants';

/**
 * Calendar midnight for date-only semantics.
 *
 * - **Moment** (e.g. from `getDateLimit`, F2F in site TZ): use `.year()/.month()/.date()` so
 *   the civil day matches the moment’s zone/offset. Projecting `toDate()` then using the
 *   browser’s local getters can move the day (e.g. UTC midnight → previous evening Central),
 *   which made `minimumDate` differ between dashboard (`moment.tz.setDefault`) and standalone.
 * - **Date** from react-datepicker: use browser local getters (the cell the user clicked).
 *
 * @param {Date | import('moment').Moment | string | number | null | undefined} input
 * @returns {Date | null}
 */
const localCivilDay = (input) => {
  if (input == null) {
    return null;
  }
  if (moment.isMoment(input)) {
    if (!input.isValid()) {
      return null;
    }
    const y = input.year();
    const m = input.month();
    const day = input.date();
    return new Date(y, m, day);
  }
  let d;
  if (input instanceof Date) {
    d = input;
  } else {
    d = new Date(input);
  }
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** @returns {number} a minus b in whole local calendar days */
const diffLocalCivilDays = (a, b) => {
  const da = localCivilDay(a);
  const db = localCivilDay(b);
  if (!da || !db) {
    return NaN;
  }
  return Math.round((da.getTime() - db.getTime()) / 86400000);
};

const AbsSuggestBox = React.memo(styled(SuggestBox)`
  && {
    ${(props) => (props['data-absolute'] ? `position: absolute;` : ``)};
    z-index: 101;
    width: inherit;
  }

  &&.suggest-box--is-disabled .suggest-box__indicators {
    display: none;
  }

  && p:not(.MuiTypography-colorTextSecondary) {
    color: ${(props) => props.theme.palette.primary.main};
    font-weight: 500;
  }
  && .MuiInput-underline:before {
    border-color: ${(props) => props.theme.shape.borderRadius}px;
  }
  @media print {
    && p:not(.MuiTypography-colorTextSecondary) {
      font-size: 1em;
      line-height: 1em;
      color: initial;
    }
    && p.MuiTypography-colorTextSecondary {
      display: none;
    }
    && .MuiInput-underline:before {
      border: none;
    }
    && .MuiSvgIcon-root {
      display: none;
    }
  }
`);

const DateDiv = styled.div`
  width: 100%;
`;

/** Clamp picked date to min/max using the same local civil-day basis as the picker. */
const clampDateToRange = (date, minDate, maxDate) => {
  const d = localCivilDay(date);
  const min = localCivilDay(minDate);
  const max = localCivilDay(maxDate);
  if (!d || !min || !max) {
    return d || min || max;
  }
  if (d.getTime() < min.getTime()) {
    return min;
  }
  if (d.getTime() > max.getTime()) {
    return max;
  }
  return d;
};

/**
 * Same rules as the historical DateSelector display logic, using calendar days so
 * minimumDate (picker) and dateValue (selected) never disagree on the allowed day.
 */
const computeDateValueFromState = (
  selectedDate,
  verbalDateLimitMoment,
  minimumDate,
  maximumDate,
) => {
  const limitDay = localCivilDay(verbalDateLimitMoment);
  const minDay = localCivilDay(minimumDate);
  const maxDay = localCivilDay(maximumDate);
  const selectedDay = selectedDate ? localCivilDay(selectedDate) : null;
  const nowDay = localCivilDay(new Date());

  let defaultDate;
  if (!selectedDay) {
    defaultDate = nowDay;
  } else if (limitDay && limitDay.getTime() > selectedDay.getTime()) {
    defaultDate = limitDay;
  } else {
    defaultDate = selectedDay;
  }

  let value =
    selectedDay && limitDay && limitDay.getTime() < selectedDay.getTime()
      ? selectedDay
      : defaultDate;

  if (minDay && value.getTime() < minDay.getTime()) {
    value = minDay;
  }
  if (maxDay && value.getTime() > maxDay.getTime()) {
    value = maxDay;
  }
  return value;
};

const PhysPickerLowZWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

/** ()fixing all messed up syntax highlighting */

/**
 * @typedef DateSelectorProps
 *  @prop {boolean} [absolutePhysPicker]
 *  @prop {number} [datePickerHeight]
 *  @prop {boolean} [hide]
 *  @prop {boolean} [blankUntilDateSelected] No default day until context has a date (MissingParams modal).
 *  @prop {Date} selectedDate
 *  @prop {string|Date} certStartDate
 *
 * @typedef {React.SFC<DateSelectorProps>} DateSelector
 */

/**
 * @type {DateSelector}
 */
export const DateSelector = React.memo(
  ({
    hide,
    certStartDate,
    verbalDate,
    patient,
    blankUntilDateSelected = false,
  }) => {
    const { dispatch } = useContext(CertDispatchContext);
    const { selectedDate } = useContext(CertContext);

    // TODO understand
    // dispatch({
    //   type: SET_DATE,
    //   payload: null,
    // });

    const [verbalDateLimit] = useState(getDateLimit(patient, certStartDate));
    // verbal date <= 2 days after the election period start date
    const timelyVerbal =
      verbalDate && certStartDate
        ? diffLocalCivilDays(verbalDate, certStartDate) <= 2
        : false;

    const maxDate = useMemo(() => localCivilDay(new Date()), []);

    // Earliest selectable day: verbal date if present, else cert/F2F limit (same as before,
    // normalized to local midnight so it matches dateValue and react-datepicker minDate).
    const minimumDate = useMemo(() => {
      if (verbalDate) {
        return localCivilDay(verbalDate);
      }
      return localCivilDay(verbalDateLimit);
    }, [verbalDate, verbalDateLimit]);

    const dateValue = useMemo(() => {
      if (blankUntilDateSelected && !selectedDate) {
        return null;
      }
      return computeDateValueFromState(
        selectedDate,
        verbalDateLimit,
        minimumDate,
        maxDate,
      );
    }, [
      blankUntilDateSelected,
      selectedDate,
      verbalDateLimit,
      minimumDate,
      maxDate,
    ]);

    useLayoutEffect(() => {
      if (!selectedDate) {
        return;
      }
      const selDay = localCivilDay(selectedDate);
      const valDay = localCivilDay(dateValue);
      if (selDay && valDay && selDay.getTime() !== valDay.getTime()) {
        dispatch({ type: SET_DATE, payload: dateValue });
      }
    }, [selectedDate, dateValue, dispatch]);

    const handleDateChange = useCallback(
      // date from react-datepicker
      (date) => {
        const nextDate = clampDateToRange(date, minimumDate, maxDate);

        dispatch({
          type: SET_VERBAL_DATE_LIMIT,
          payload: null,
        });
        dispatch({
          type: SET_LATE_CERT,
          payload: false,
        });
        // cert date entered is 15 days prior to start of cert
        const diff =
          certStartDate && nextDate
            ? diffLocalCivilDays(certStartDate, nextDate) > 15
            : false;
        // date entered is 2 days after start of cert && no or late verbal date
        const lateCert =
          certStartDate && nextDate
            ? diffLocalCivilDays(nextDate, certStartDate) > 2 && !timelyVerbal
            : false;
        if (diff) {
          dispatch({
            type: SET_VERBAL_DATE_LIMIT,
            payload: verbalDateLimit,
          });
        }
        if (lateCert) {
          dispatch({
            type: SET_LATE_CERT,
            payload: lateCert,
          });
        }
        // Always dispatch to ensure fetchCertDoctorsWithDate runs when date changes
        // (e.g. in MissingParams when user selects/changes certification date)
        const sel =
          selectedDate && nextDate ? localCivilDay(selectedDate) : null;
        const nxt = nextDate ? localCivilDay(nextDate) : null;
        const datesDiffer =
          !selectedDate ||
          !nextDate ||
          !sel ||
          !nxt ||
          sel.getTime() !== nxt.getTime();
        if (datesDiffer) {
          dispatch({
            type: SET_DATE,
            payload: nextDate,
          });
        }
      },
      [
        dispatch,
        certStartDate,
        verbalDateLimit,
        selectedDate,
        timelyVerbal,
        minimumDate,
        maxDate,
      ],
    );
    if (hide) {
      return null;
    }
    return (
      <DateDiv>
        <ReactDatePicker
          popperPosition="fixed"
          popperInset="unset"
          popperTransform="unset"
          selectedDate={dateValue}
          onChange={handleDateChange}
          minimumDate={minimumDate}
          maximumDate={maxDate}
        />
      </DateDiv>
    );
  },
);

/**
 * @typedef PhysSelectorProps
 *  @prop {string} [id]
 *  @prop {Record<string, any>[]} doctors
 *  @prop {boolean} [disabled]
 *  @prop {boolean} [absolute]
 *  @prop {any} [labelRef]
 *  @prop {any} [selectedDoctor]
 *
 * @typedef {React.SFC<PhysSelectorProps>} PhysSelector
 */

/**
 * @type {PhysSelector}
 */
export const PhysSelector = React.memo(
  ({ id, absolute, disabled, doctors, hide, labelRef, selectedDoctor }) => {
    /**
     *  TODO: This logic should be in SuggestBox itself; something isn't working with their
     *  TODO:   'auto' prop. Possibly related to the custom components
     */
    const [distanceFromBottom, setDistanceFromBottom] = useState(301);
    const [menuPlacement, setMenuPlacement] = useState('bottom');
    const { dispatch } = useContext(CertDispatchContext);
    const handleChange = useCallback(
      (doctor) => {
        dispatch({ type: SET_DOCTOR, payload: doctor });
      },
      [dispatch],
    );
    useEffect(() => {
      let mounted = true;
      if (mounted && labelRef && labelRef.current instanceof Element) {
        const { bottom } = labelRef.current.getBoundingClientRect();
        setDistanceFromBottom(window.innerHeight - bottom);
      }
      return () => {
        mounted = false;
      };
    }, [labelRef, setDistanceFromBottom]);
    useEffect(() => {
      const newPlacement = distanceFromBottom < 300 ? 'top' : 'bottom';
      if (newPlacement !== menuPlacement) {
        setMenuPlacement(newPlacement);
      }
    }, [menuPlacement, distanceFromBottom, setMenuPlacement]);
    if (hide) {
      return null;
    }
    return (
      <AbsSuggestBox
        inputId={id}
        data-absolute={absolute}
        disabled={disabled}
        handleChange={handleChange}
        menuPlacement={menuPlacement}
        menuPosition="fixed"
        value={selectedDoctor}
        options={doctors}
      />
    );
  },
);

export const withSelectors = (WrappedComponent) => ({
  absolutePhysPicker,
  alreadyCertified,
  datePickerHeight,
  physSelectorId,
  physContainerRef,
  isDoctor,
  fetchCertDoctorsOnDateChange = false,
  hasHospicePhysician = false,
  /** When true, physician dropdown is display-only (e.g. main cert print footer). */
  disablePhysPicker = false,
  /** When true, cert date field stays empty until user picks (MissingParams modal). */
  blankUntilDateSelected = false,
  open: dialogOpen,
  ...props
}) => {
  const {
    doctors,
    formType,
    pickerDisabled,
    selectedDoctor,
    selectedDate,
    certStartDate,
    verbalDateLimit,
    lateCert,
    dimensions,
    embedded,
    verbalDate,
    selectedPatient,
  } = useContext(CertContext);
  const { isDoctor: appContextIsDoctor, agency: appContextAgency } = useContext(
    AppContext,
  );
  const effectiveIsDoctor = isDoctor ?? appContextIsDoctor;
  const { fetchCertDoctorsWithDate } = useContext(CertDispatchContext);
  const [hideSelectors, setHideSelectors] = useState(alreadyCertified);

  // For non-physicians in MissingParams: fetch cert doctors when date is selected or dialog opens
  useEffect(() => {
    const shouldFetch =
      fetchCertDoctorsOnDateChange &&
      effectiveIsDoctor === false &&
      selectedDate &&
      dialogOpen &&
      fetchCertDoctorsWithDate;
    if (shouldFetch)
      fetchCertDoctorsWithDate(selectedDate, selectedPatient, appContextAgency);
  }, [
    fetchCertDoctorsOnDateChange,
    effectiveIsDoctor,
    selectedDate,
    selectedPatient,
    dialogOpen,
    fetchCertDoctorsWithDate,
    appContextAgency,
  ]);

  useEffect(() => {
    const shouldHide = alreadyCertified;
    if (shouldHide !== hideSelectors) {
      setHideSelectors(shouldHide);
    }
  }, [hideSelectors, appContextIsDoctor, alreadyCertified]);

  // For non-physicians: PhysPicker disabled until certification date is entered AND doctors are loaded
  // In MissingParams: ignore pickerDisabled (user must select). Elsewhere: respect it.
  const isNonPhysNeedingInput =
    effectiveIsDoctor === false && !selectedDate && !doctors[formType]?.length;
  let physPickerDisabled;
  if (disablePhysPicker) {
    physPickerDisabled = true;
  } else if (fetchCertDoctorsOnDateChange) {
    physPickerDisabled = isNonPhysNeedingInput;
  } else {
    physPickerDisabled = pickerDisabled || isNonPhysNeedingInput;
  }

  const doctorsForPicker =
    effectiveIsDoctor === false && !selectedDate ? [] : doctors[formType];

  const physPickerProps = {
    key: 'selectors-phys-selector',
    hide: hideSelectors,
    id: physSelectorId,
    labelRef: physContainerRef,
    absolute: absolutePhysPicker,
    disabled: physPickerDisabled,
    selectedDoctor,
    doctors: doctorsForPicker,
  };
  const PhysPickerContent = <PhysSelector {...physPickerProps} />;
  const usePhysPickerLowZWrapper =
    fetchCertDoctorsOnDateChange || hasHospicePhysician;

  return (
    <WrappedComponent
      {...props}
      open={dialogOpen}
      physContainerRef={physContainerRef}
      dimensions={dimensions}
      embedded={embedded}
      selectedDoctor={selectedDoctor}
      verbalDateLimit={verbalDateLimit}
      lateCert={lateCert}
      isDoctor={effectiveIsDoctor}
      PhysPicker={
        usePhysPickerLowZWrapper ? (
          <PhysPickerLowZWrapper>{PhysPickerContent}</PhysPickerLowZWrapper>
        ) : (
          PhysPickerContent
        )
      }
      DatePicker={
        <DateSelector
          key="selectors-date-selector"
          hide={hideSelectors}
          absolutePhysPicker={absolutePhysPicker}
          datePickerHeight={datePickerHeight}
          certStartDate={certStartDate}
          verbalDate={verbalDate}
          patient={selectedPatient}
          blankUntilDateSelected={blankUntilDateSelected}
        />
      }
    />
  );
};
